import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, CheckSquare, Square, Image as ImageIcon, Loader2, XCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../lib/api';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

const PLACEHOLDER = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23111"/><text x="50%" y="50%" fill="%23333" font-size="14" text-anchor="middle" dy=".3em">No image</text></svg>';

export const AdminGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await api.get('/admin/gallery');
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load gallery'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      const res = await api.post('/admin/gallery/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`Uploaded ${res.data.uploaded} photo(s)`);
      fetchPhotos();
    } catch { toast.error('Upload failed — try uploading fewer photos at a time'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === photos.length) setSelected(new Set());
    else setSelected(new Set(photos.map(p => p.id)));
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} photo(s)?`)) return;
    try {
      await api.post('/admin/gallery/bulk-delete', { photo_ids: Array.from(selected) });
      toast.success(`Deleted ${selected.size} photo(s)`);
      setSelected(new Set());
      fetchPhotos();
    } catch { toast.error('Delete failed'); }
  };

  const handleDeleteAll = async ({ confirmation_phrase, acknowledged }) => {
    setDeleteAllLoading(true);
    try {
      const res = await api.post('/admin/gallery/delete-all', { confirmation_phrase, acknowledged });
      toast.success(`Deleted ${res.data.deleted} photo(s). Gallery is empty.`);
      setSelected(new Set());
      setShowDeleteAllModal(false);
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete all failed');
    } finally { setDeleteAllLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success('Photo deleted');
      fetchPhotos();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6" data-testid="admin-gallery-page">
      <Toaster position="top-right" theme="dark" />

      <DangerConfirmModal
        open={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Gallery Photos"
        description="This will permanently delete every photo in the gallery. This action cannot be undone."
        confirmPhrase="DELETE ALL GALLERY PHOTOS"
        loading={deleteAllLoading}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="gallery-title">Gallery</h1>
          <p className="text-white/40 text-sm mt-1">{photos.length} photo(s)</p>
        </div>
        <div className="flex items-center gap-2">
          {photos.length > 0 && (
            <button onClick={() => setShowDeleteAllModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-colors"
              data-testid="delete-all-gallery-btn">
              <XCircle className="w-4 h-4" /> Delete All
            </button>
          )}
          <label className="flex items-center gap-2 px-4 py-2.5 bg-[#00D4FF] text-black text-sm font-semibold cursor-pointer hover:bg-[#00B8E0] transition-colors" data-testid="upload-photos-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload Photos'}
            <input type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Toolbar */}
      {photos.length > 0 && (
        <div className="flex items-center gap-3 mb-4 text-sm">
          <button onClick={selectAll} className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors" data-testid="select-all-btn">
            {selected.size === photos.length ? <CheckSquare className="w-4 h-4 text-[#00D4FF]" /> : <Square className="w-4 h-4" />}
            {selected.size === photos.length ? 'Deselect All' : 'Select All'}
          </button>
          {selected.size > 0 && (
            <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-red-400 hover:text-red-300 transition-colors" data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete {selected.size} selected
            </button>
          )}
          <span className="text-white/25 ml-auto">Click photo to select</span>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-white/10" />
          <p className="text-white/40">No photos yet. Upload some!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2" data-testid="gallery-grid">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className={`relative group aspect-square cursor-pointer border-2 transition-all ${
                selected.has(photo.id) ? 'border-[#00D4FF]' : 'border-transparent hover:border-white/20'
              }`}
              onClick={() => toggleSelect(photo.id)}
              data-testid={`gallery-photo-${photo.id}`}
            >
              <img
                src={photo.url || PLACEHOLDER}
                alt={photo.original_name || 'Gallery photo'}
                className="w-full h-full object-cover bg-[#111]"
                loading="lazy"
                onError={(e) => { e.target.src = PLACEHOLDER; }}
              />
              <div className={`absolute top-2 left-2 ${selected.has(photo.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                {selected.has(photo.id)
                  ? <CheckSquare className="w-5 h-5 text-[#00D4FF]" />
                  : <Square className="w-5 h-5 text-white/70" />
                }
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                className="absolute top-2 right-2 p-1 bg-black/60 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-300 transition-all"
                data-testid={`delete-photo-${photo.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
